import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../utils/api'
import { useSitesStore } from './sites'

// Channel-first Social store.
//
// Channels come from Postiz (with per-channel performance); the feed is the
// existing bwai_social_posts data, EXPLODED into one entry per (post × channel)
// so historical multi-platform posts appear under each channel. Creating a post
// reuses the existing generate + publish endpoints, seeded from a capability.
export const useSocialStore = defineStore('social', () => {
  const channels = ref([])
  const channelsStatus = ref(null)
  const manageUrl = ref(null)
  const loadingChannels = ref(false)

  const posts = ref([]) // raw bwai_social_posts rows
  const loadingFeed = ref(false)
  const error = ref(null)

  const errMsg = (e) => e?.response?.data?.error || e?.message || 'Something went wrong'

  async function loadChannels(siteId) {
    loadingChannels.value = true
    try {
      const { data } = await api.get(`/sites/${siteId}/social/channels`)
      channels.value = data.channels || []
      channelsStatus.value = data.status
      manageUrl.value = data.manage_url || null
    } catch (e) {
      error.value = errMsg(e)
      channelsStatus.value = 'unavailable'
    } finally {
      loadingChannels.value = false
    }
  }

  async function loadFeed(siteId) {
    loadingFeed.value = true
    try {
      const { data } = await api.get(`/sites/${siteId}/social-posts`)
      posts.value = data.posts || data.socialPosts || []
    } catch (e) {
      error.value = errMsg(e)
      posts.value = []
    } finally {
      loadingFeed.value = false
    }
  }

  // Channel platform ids (Postiz, e.g. 'linkedin-page') can differ from the ids
  // stored on historical posts (the old composer used 'linkedin', 'twitter').
  // Accept either when matching a channel to its posts.
  const PLATFORM_ALIASES = {
    'linkedin-page': ['linkedin-page', 'linkedin'],
    linkedin: ['linkedin', 'linkedin-page'],
    x: ['x', 'twitter'],
    twitter: ['twitter', 'x'],
  }
  const aliasesFor = (platform) => PLATFORM_ALIASES[platform] || [platform]

  // Channel platform (Postiz) -> the internal id the generator / image / publish
  // pipeline uses (posts are keyed by this).
  const toInternal = (platform) => ({ 'linkedin-page': 'linkedin', x: 'twitter' }[platform] || platform)

  // Explode raw posts into per-channel feed entries. A publication for a
  // platform => published (+ its time); otherwise it's a draft dated by created.
  function feedFor(platform) {
    const accepted = aliasesFor(platform)
    const items = []
    for (const p of posts.value) {
      const platforms = p.platforms || p.targetPlatforms || []
      // the key this post actually used for this channel (may be an alias)
      const key = platforms.find((pl) => accepted.includes(pl))
      if (!key) continue
      const variants = p.variants_by_platform || p.variantsByPlatform || {}
      const images = p.images_by_platform || p.imagesByPlatform || {}
      const pubs = Array.isArray(p.publications) ? p.publications : []
      const pub = pubs.find((x) => accepted.includes(x?.platformId) || accepted.includes(x?.platform))
      const variant = variants[key]
      const content = Array.isArray(variant) ? (variant[0]?.text || variant[0] || '') : (variant?.text || variant || p.prompt || '')
      items.push({
        id: `${p.id}:${platform}`,
        postId: p.id,
        platform,
        content: typeof content === 'string' ? content : JSON.stringify(content),
        image: images[key]?.url || images[key] || null,
        status: pub ? (pub.status || 'published') : 'draft',
        at: pub?.publishedAt || pub?.scheduledAt || p.created_at || p.createdAt,
        prompt: p.prompt,
      })
    }
    return items.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
  }

  // Draft a post for one channel, seeded from a capability. Reuses the existing
  // generate pipeline (prompt + single platform).
  async function draftFromCapability(siteId, platform, capability, extra = '') {
    const sites = useSitesStore()
    const internal = toInternal(platform)
    const brief = [
      `Write one ${internal} post about this product capability.`,
      `Capability: ${capability.capability_name || capability.name}.`,
      capability.description ? `What it does: ${capability.description}.` : '',
      extra ? `Extra direction: ${extra}` : '',
    ].filter(Boolean).join('\n')
    const data = await sites.generateSocialPosts(siteId, { prompt: brief, platforms: [internal], variantsPerPlatform: 1 })
    return { ...data, internalPlatform: internal }
  }

  // Conversational refine — returns revised copy (no persistence).
  async function refine(siteId, platform, content, instruction) {
    const { data } = await api.post(`/sites/${siteId}/social/refine`, { platform, content, instruction })
    return data.content
  }

  // Generate an image for a drafted post (reuses the existing endpoint).
  async function generateImage(siteId, postId, platform, style = 'creative') {
    const { data } = await api.post(`/sites/${siteId}/social-posts/generate-image`, { postId, platformId: platform, style })
    return data
  }

  async function publish(siteId, postId, { integrationId, platformId, variantTier, type = 'now', date = null, mediaItems = [], content = null }) {
    const body = { integrationId, platformId, variantTier, type }
    if (type === 'schedule' && date) body.date = new Date(date).toISOString()
    if (mediaItems.length) body.mediaItems = mediaItems
    if (content) body.content = content
    const { data } = await api.post(`/sites/${siteId}/social-posts/${postId}/publish`, body)
    return data
  }

  const connectedPlatforms = computed(() => channels.value.map((c) => c.platform))

  return {
    channels, channelsStatus, manageUrl, loadingChannels, posts, loadingFeed, error,
    connectedPlatforms, toInternal,
    loadChannels, loadFeed, feedFor, draftFromCapability, refine, generateImage, publish,
  }
})
